// TODO: lift subscriptions to Dashboard level
import React, { useEffect, useMemo, useState } from 'react'
import { useYear } from '../auth/YearContext'
import {
  subscribeYear,
  subscribeReceipts,
  aggregateReceipts,
  aggregateReceiptsByCategory,
  receiptIsMissingVat,
  type CloudYearDoc,
  type CloudReceipt,
} from '../lib/cloudStore'
import { AiVatBatchDialog } from './AiVatBatchDialog'
import { ReRunAiDialog } from './ReRunAiDialog'
import { MonthDetailDialog } from './MonthDetailDialog'
import { vatBalanceYTD } from '../data/store'
import type { CompanyYearData } from '../data/store'
import { WinButtons, currentSize, type CardSizeProps } from './WinButtons'
import { smoothPath } from '../lib/chart'
import { emptyMonths } from '../data/store'

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'Maj', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec']
const fmt = (n: number) => n.toLocaleString('sv-SE', { maximumFractionDigits: 0 })

interface Props extends CardSizeProps { companyId: string; companyName: string }

export function MomsBalanceCard(props: Props) {
  const { companyId, companyName } = props
  const size = currentSize(props)
  const { year } = useYear()
  const [data, setData] = useState<CloudYearDoc>({ year, months: emptyMonths() })
  const [receipts, setReceipts] = useState<CloudReceipt[]>([])
  const [showAiBatch, setShowAiBatch] = useState(false)
  const [showReRun, setShowReRun] = useState(false)
  // 2026-05-23: Klick på månad → visa top 10 moms in/ut för den månaden
  const [detailMonth, setDetailMonth] = useState<number | null>(null)

  useEffect(() => {
    try {
      return subscribeYear(companyId, year, setData)
    } catch {
      /* not authed yet */
      return () => {}
    }
  }, [companyId, year])

  useEffect(() => {
    try {
      return subscribeReceipts(companyId, year, setReceipts)
    } catch {
      /* not authed */
      return () => {}
    }
  }, [companyId, year])

  const receiptMonths = useMemo(() => aggregateReceipts(receipts), [receipts])
  const catMonths = useMemo(() => aggregateReceiptsByCategory(receipts), [receipts])

  // FAS A (2026-05-21): räkna poster utan moms-data (för varningsbanner)
  const missingVatList = useMemo(
    () => receipts.filter((r) => receiptIsMissingVat(r)),
    [receipts]
  )
  const missingVatCount = missingVatList.length

  // 2026-05-21 (Re-run AI feature): poster som har PDF i Storage men saknar
  // moms — dessa kan AI:n köras om på UTAN att användaren behöver ladda upp
  // filerna igen. Skiljer från missingVatList som även innehåller poster
  // utan storagePath (som kräver upload).
  const reRunList = useMemo(
    () => missingVatList.filter((r) => !!r.storagePath),
    [missingVatList]
  )
  const reRunCount = reRunList.length
  const sieMonths = data.months.length === 12 ? data.months : emptyMonths()

  // Merge SIE + kvitto-moms: SIE tar prio om det finns, annars kvitto-beräknad moms
  const months = useMemo(() => {
    return sieMonths.map((sie, i) => {
      const rec = receiptMonths[i]
      const sieHasVat = sie.vatIn !== 0 || sie.vatOut !== 0
      return {
        month: i,
        vatIn: sieHasVat ? sie.vatIn : rec.vatIn,
        vatOut: sieHasVat ? sie.vatOut : rec.vatOut,
        income: sie.income || rec.income,
        expense: sie.expense || rec.expense,
      }
    })
  }, [sieMonths, receiptMonths])

  const hasReceiptVat = receiptMonths.some((m) => m.vatIn !== 0 || m.vatOut !== 0)
  const hasSieVat = sieMonths.some((m) => m.vatIn !== 0 || m.vatOut !== 0)
  // Källa: moms kommer från BÅDA — kundfakturor (ut) + leverantörsfakturor & utlägg (in)
  const dataSource = hasSieVat && hasReceiptVat ? 'SIE + Fakturor + Kvitton' : hasSieVat ? 'SIE' : hasReceiptVat ? 'Fakturor + Kvitton' : null

  const { vatIn, vatOut, netToPay } = vatBalanceYTD({ year, months } as CompanyYearData)
  const color = netToPay >= 0 ? 'value--neg' : 'value--pos'
  const label = netToPay >= 0 ? 'Att betala till Skatteverket' : 'Att \u00e5terf\u00e5 fr\u00e5n Skatteverket'

  const now = new Date()
  const uptoMonth = year === now.getFullYear() ? now.getMonth() : 11

  const visible = months.slice(0, uptoMonth + 1)
  const hasAnyData = visible.some((m) => m.vatIn !== 0 || m.vatOut !== 0)

  // Netto momsbalans per månad — visar vad man betalar/återfår varje månad
  const netVatPerMonth = visible.map((m) => m.vatOut - m.vatIn)
  const maxNet = Math.max(1, ...netVatPerMonth.map(Math.abs))

  const W = 360
  const H = 80
  const pad = 6

  if (size === "min") {
    return (
      <div className="card card--collapsed">
        <div className="card__header-row">
          <span className="card__label" style={{ margin: 0 }}>Momsbalans</span>
          <span className={'card__mini-value ' + color}>{fmt(Math.abs(netToPay))} kr</span>
          <WinButtons current="min" {...props} />
        </div>
      </div>
    )
  }

  return (
    <div className={"card card--accent" + (size === "med" ? " card--medium" : "")}>
      <div className="card__header-row">
        <div className="card__label" style={{ margin: 0 }}>{'Momsbalans nu (sedan ' + year + '-01-01)'}</div>
        <WinButtons current={size} {...props} />
      </div>
      <div className={'card__value ' + color}>{fmt(Math.abs(netToPay))} kr</div>
      <div className="card__sub">{label}</div>

      {/* Explicit ber\u00e4kning: Moms ut - Moms in = Att betala, j\u00e4mf\u00f6rt med skattekonto-buffer.
          Tidigare visades bara siffrorna utan formel \u2014 nu syns hela kedjan. */}
      {(() => {
        const skattekonto = data.skattekontoSaldo ?? 0
        const nettoEfterSkattekonto = netToPay - skattekonto
        const bufferOk = skattekonto >= netToPay
        const rowStyle: React.CSSProperties = {
          display: 'flex',
          justifyContent: 'space-between',
          padding: '2px 0',
          fontSize: '0.85rem',
        }
        const labelStyle: React.CSSProperties = { color: 'var(--text-muted)' }
        const valStyle: React.CSSProperties = { color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }
        return (
          <div style={{ marginTop: '0.8rem', borderTop: '1px solid var(--border)', paddingTop: '0.5rem' }}>
            <div style={rowStyle}>
              <span style={labelStyle}>{'Moms ut (kundfakturor)'}</span>
              <span style={valStyle}><b>{fmt(vatOut)}</b>{' kr'}</span>
            </div>
            <div style={rowStyle}>
              <span style={labelStyle}>{'Moms in (lev.fakt + utl\u00e4gg)'}</span>
              <span style={valStyle}><b>{'\u2212' + fmt(vatIn)}</b>{' kr'}</span>
            </div>
            <div style={{ ...rowStyle, borderTop: '1px dashed var(--border)', marginTop: 2, paddingTop: 4 }}>
              <span style={{ ...labelStyle, fontWeight: 600 }}>{'Att betala till SKV'}</span>
              <span style={{ ...valStyle, fontWeight: 700, color: netToPay >= 0 ? 'var(--danger)' : 'var(--success)' }}>
                {fmt(netToPay)}{' kr'}
              </span>
            </div>
            <div style={{ ...rowStyle, marginTop: 4 }}>
              <span style={labelStyle}>{'Skattekontosaldo (buffer)'}</span>
              <span style={valStyle}>{fmt(skattekonto)}{' kr'}</span>
            </div>
            <div style={{ ...rowStyle, borderTop: '1px dashed var(--border)', marginTop: 2, paddingTop: 4 }}>
              <span style={{ ...labelStyle, fontWeight: 600 }}>
                {nettoEfterSkattekonto > 0 ? 'Behov att f\u00f6ra \u00f6ver' : '\u00d6verskott efter SKV'}
              </span>
              <span style={{
                ...valStyle,
                fontWeight: 700,
                color: bufferOk ? 'var(--success)' : 'var(--danger)',
              }}>
                {fmt(Math.abs(nettoEfterSkattekonto))}{' kr'}
              </span>
            </div>
            {dataSource && (
              <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 6, opacity: 0.8 }}>
                {'K\u00e4lla: ' + dataSource}
              </div>
            )}
          </div>
        )
      })()}

      {/* FAS A+B (2026-05-21): varning n\u00e4r poster saknar moms-data + knapp
          som \u00f6ppnar AiVatBatchDialog (FAS B) f\u00f6r att fylla i moms via xAI Grok. */}
      {missingVatCount > 0 && (
        <div style={{
          marginTop: '0.6rem',
          padding: '0.55rem 0.7rem',
          background: 'rgba(245,158,11,0.10)',
          border: '1px solid #f59e0b',
          borderRadius: 6,
          display: 'flex',
          alignItems: 'center',
          gap: '0.6rem',
          flexWrap: 'wrap',
        }}>
          <span style={{ fontSize: '0.82rem', color: 'var(--text)', flex: 1, minWidth: 200 }}>
            {'\u26a0 ' + missingVatCount + ' poster saknar moms-data (visas som 0 kr moms in)'}
          </span>
          <button
            onClick={() => setShowAiBatch(true)}
            style={{
              background: '#f59e0b',
              color: '#fff',
              border: 'none',
              borderRadius: 4,
              padding: '0.3rem 0.7rem',
              fontSize: '0.78rem',
              fontWeight: 600,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            {'\ud83e\udd16 K\u00f6r AI-analys p\u00e5 ' + missingVatCount + ' poster'}
          </button>
        </div>
      )}

      {/* 2026-05-21 (Re-run AI): extra knapp f\u00f6r poster som redan har PDF
          i Storage \u2014 anv\u00e4ndaren slipper ladda upp filerna igen. */}
      {reRunCount > 0 && (
        <div style={{
          marginTop: '0.35rem',
          padding: '0.45rem 0.7rem',
          background: 'rgba(59,130,246,0.10)',
          border: '1px solid #3b82f6',
          borderRadius: 6,
          display: 'flex',
          alignItems: 'center',
          gap: '0.6rem',
          flexWrap: 'wrap',
        }}>
          <span style={{ fontSize: '0.78rem', color: 'var(--text)', flex: 1, minWidth: 200 }}>
            {'\ud83d\udcc4 ' + reRunCount + ' poster har PDF i Storage \u2014 ingen upload n\u00f6dv\u00e4ndig'}
          </span>
          <button
            onClick={() => setShowReRun(true)}
            style={{
              background: '#3b82f6',
              color: '#fff',
              border: 'none',
              borderRadius: 4,
              padding: '0.3rem 0.7rem',
              fontSize: '0.78rem',
              fontWeight: 600,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            {'\ud83d\udd01 K\u00f6r AI igen p\u00e5 ' + reRunCount + ' poster (utan upload)'}
          </button>
        </div>
      )}

      <AiVatBatchDialog
        open={showAiBatch}
        onClose={() => setShowAiBatch(false)}
        companyId={companyId}
        companyName={companyName}
        receipts={missingVatList}
      />

      <ReRunAiDialog
        open={showReRun}
        onClose={() => setShowReRun(false)}
        companyId={companyId}
        companyName={companyName}
        receipts={reRunList}
      />

      <div className="card__divider" />

      <div className="card__label">{'VAT netto per m\u00e5nad'}</div>
      {hasAnyData ? (
        <>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', textAlign: 'right', marginBottom: 2 }}>
            {'Max: \u00b1' + fmt(maxNet) + ' kr'}
          </div>
          <svg viewBox={'0 0 ' + W + ' ' + H} style={{ width: '100%', height: 80, marginTop: 2, cursor: 'pointer' }} preserveAspectRatio="none">
            {/* Nollinje */}
            <line x1={pad} y1={H / 2} x2={W - pad} y2={H / 2} stroke="var(--border)" strokeWidth="1" strokeDasharray="4 3" />
            {/* Netto momsbalans: ovanför noll = betala (röd), under = återfå (grön) */}
            {(() => {
              const pts: [number, number][] = netVatPerMonth.map((v, i) => {
                const x = netVatPerMonth.length === 1 ? W / 2 : pad + (i / (netVatPerMonth.length - 1)) * (W - pad * 2)
                // Positivt (betala) → uppåt från mitten = röd sida, negativt (återfå) → nedåt = grön sida
                const y = H / 2 - (v / maxNet) * (H / 2 - pad)
                return [x, y] as [number, number]
              })
              const zeroY = H / 2
              // Fill area: grön under noll, röd ovanför
              const fillPath = 'M' + pts[0][0] + ',' + zeroY + ' ' +
                pts.map(p => 'L' + p[0].toFixed(1) + ',' + p[1].toFixed(1)).join(' ') +
                ' L' + pts[pts.length - 1][0] + ',' + zeroY + 'Z'
              // Klickbara kolumner — bredare träffyta än enbart cirkeln
              const colW = (W - pad * 2) / Math.max(1, netVatPerMonth.length)
              return (
                <>
                  <path d={fillPath} fill="#ef4444" opacity="0.12" />
                  <path d={smoothPath(pts)} fill="none" stroke="#f59e0b" strokeWidth="2.5" />
                  {pts.map((p, i) => (
                    <circle key={'c' + i} cx={p[0]} cy={p[1]} r="3"
                      fill={netVatPerMonth[i] >= 0 ? '#ef4444' : '#10b981'}
                      stroke="var(--bg-card)" strokeWidth="1.5" />
                  ))}
                  {/* Transparenta klick-overlay-rektanglar per månad (osynliga men breda) */}
                  {pts.map((p, i) => (
                    <rect
                      key={'r' + i}
                      x={Math.max(0, p[0] - colW / 2)}
                      y={0}
                      width={colW}
                      height={H}
                      fill="transparent"
                      style={{ cursor: 'pointer' }}
                      onClick={() => setDetailMonth(i)}
                    >
                      <title>{MONTHS[i] + ': klicka för topp 10 moms in/ut'}</title>
                    </rect>
                  ))}
                </>
              )
            })()}
            {/* Labels */}
            <text x={W - pad} y={pad + 3} fill="var(--text-muted)" fontSize="8" textAnchor="end" opacity="0.6">{'Betala \u2191'}</text>
            <text x={W - pad} y={H - pad + 2} fill="var(--text-muted)" fontSize="8" textAnchor="end" opacity="0.6">{'\u00c5terf\u00e5 \u2193'}</text>
          </svg>
        </>
      ) : (
        <div style={{ height: 80, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: '0.8rem', opacity: 0.5 }}>
          {'Ingen data'}
        </div>
      )}
      <div className="sparkline__label-row">
        {MONTHS.slice(0, uptoMonth + 1).map((m) => (
          <span key={m} className="sparkline__label">{m}</span>
        ))}
      </div>

      <div className="months">
        {/* Header — månadsnamn + VAT netto + Kund/Lev/Utlägg (moms) */}
        <div className="months__row months__row--head">
          <span className="months__cell months__cell--name">{''}</span>
          <span className="months__cell">{'VAT netto'}</span>
          <span className="months__cell">{'Kund'}</span>
          <span className="months__cell">{'Lev'}</span>
          <span className="months__cell">{'Utlägg'}</span>
        </div>
        {/* En rad per månad — alltid alla månader t.o.m. uptoMonth, även om tomma */}
        {visible.map((m, i) => {
          const net = m.vatOut - m.vatIn
          const cat = catMonths[i]
          const empty = m.vatIn === 0 && m.vatOut === 0
          return (
            <div
              className="months__row"
              key={i}
              onClick={() => setDetailMonth(i)}
              style={{ cursor: 'pointer', opacity: empty ? 0.4 : 1 }}
              title={'Klicka för topp 10 moms in/ut ' + MONTHS[i]}
            >
              <span className="months__cell months__cell--name">{MONTHS[i]}</span>
              <span
                className="months__cell months__cell--net"
                style={{ color: empty ? 'var(--text-muted)' : net >= 0 ? 'var(--danger)' : 'var(--success)' }}
              >
                {empty ? '—' : fmt(net)}
              </span>
              <span className="months__cell months__cell--muted">
                {cat.kund.vat ? fmt(cat.kund.vat) : '—'}
              </span>
              <span className="months__cell months__cell--muted">
                {cat.lev.vat ? '−' + fmt(cat.lev.vat) : '—'}
              </span>
              <span className="months__cell months__cell--muted">
                {cat.utl.vat ? '−' + fmt(cat.utl.vat) : '—'}
              </span>
            </div>
          )
        })}
      </div>

      {detailMonth != null && (
        <MonthDetailDialog
          month={detailMonth}
          year={year}
          companyName={companyName}
          receipts={receipts}
          onClose={() => setDetailMonth(null)}
        />
      )}

      {!hasAnyData && (
        <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '0.5rem' }}>
          {'Ingen momsdata inlagd \u00e4n f\u00f6r ' + year + '. Importera SIE-fil via \ud83d\udce5 Import.'}
        </div>
      )}
    </div>
  )
}
